/**
 * @fileoverview JS - Class - RasterLayer.js
 * @author Adam Lemire
 * @since 2019/06/13
 * @version 1.0
 * @summary Dernières modifications : 2023/03/27
 */

import Layer from './Layer.js';
import TileLayer from 'ol/layer/Tile.js';
import TileWMS from 'ol/source/TileWMS.js';
import OSM from 'ol/source/OSM.js';

/** @enum {string} */
export const SourceType = {
  WMS: 'WMS',
  OSM: 'OSM',
  NationalMap: 'NationalMap'
};

const NOM_COUCHE_RASTER_PAR_DÉFAUT = 'Open Street Map';

/**
 * Classe RasterLayer représente une couche cartographique de type raster - raster provenant de geoserver seulement pour l'instant
 * @extends Layer
 */
export default class RasterLayer extends Layer {
  /**
   * @example const rasterLayer = new RasterLayer({
   *  id = 0,
      name: name,
      sourceUrl: url,
      sourceName: 'Open Street Map',
      sourceType: SourceType.OSM,
      jsonObject: false
    });
   * @param { object } options
   * @param { number } options.id
   * @param { string } options.name
   * @param { string } options.sourceUrl
   * @param { string } options.sourceName
   * @param { SourceType } options.sourceType
   * @param { JSON } options.jsonObject
   */
  constructor ({
    id = 0,
    name = NOM_COUCHE_RASTER_PAR_DÉFAUT,
    sourceUrl = '',
    sourceName = NOM_COUCHE_RASTER_PAR_DÉFAUT,
    sourceType = SourceType.OSM,
    jsonObject = false
  }) {
    if (jsonObject) {
      super(jsonObject.id, jsonObject.name, jsonObject.visible);
      this._créerDepuisJSON(jsonObject);
    } else {
      super(id, name);
      /** @type { SourceType } */
      this._sourceType = sourceType;
      /** @type { string } */
      this._sourceUrl = sourceUrl;
      /** @type { string } */
      this._sourceName = sourceName;
      /** @type { number } */
      this._zIndex = 0;
      /** @type { number } */
      this._opacity = 1;
    }
    /** @type { TileLayer } */
    this._olLayer = this._créerOlLayer();
  }

  get olLayer () { return this._olLayer; }

  get zIndex () { return this._zIndex; }

  set zIndex (zIndex) { this._zIndex = zIndex; }

  get sourceName () { return this._sourceName; }

  get opacity () { return this._opacity; }

  set opacity (opacity) {
    const opacityNumber = Math.round(parseFloat(opacity) * 100) / 100;
    this._opacity = opacityNumber;
    this._olLayer.setOpacity(opacityNumber);
  }

  /*
   * METHODS
   */

  _créerOlLayer () {
    const _this = this;
    return new TileLayer({
      source: _this._créerSource(),
      opacity: _this._opacity
    });
  }

  _créerSource () {
    const _this = this;
    switch (this._sourceType) {
      case SourceType.WMS:
        return new TileWMS({
          url: _this._sourceUrl,
          params: {
            LAYERS: _this._sourceName,
            TILED: true
          }
        });
      case SourceType.OSM:
        return new OSM();
      case SourceType.NationalMap:
        return new TileWMS({
          url: _this._sourceUrl,
          params: {
            LAYERS: 0
          }
        });
      default:
        console.log('problème : RasterLayer._créerSource()');
    }
  }

  /*
   * JSON Save / load
   */

  toJSON () {
    return {
      id: this.id,
      name: this.name,
      visible: this.isVisible,
      sourceName: this._sourceName,
      sourceUrl: this._sourceUrl,
      sourceType: this._sourceType,
      zIndex: this._zIndex,
      opacity: this._opacity
    };
  }

  _créerDepuisJSON (jsonRasterLayer) {
    this._sourceName = jsonRasterLayer.sourceName;
    this._sourceUrl = jsonRasterLayer.sourceUrl;
    this._sourceType = jsonRasterLayer.sourceType;
    this._opacity = jsonRasterLayer.opacity;
    this._zIndex = jsonRasterLayer.zIndex;
  }
}
